import { z } from 'zod'

// ── Field Mapping schema ──────────────────────────────────────────────────────

export const fieldMappingSchema = z.object({
  platformField: z.string(),
  crmField:      z.string().min(1, 'CRM field is required'),
  transform:     z.string().optional(),
  // Fixed value chosen by the user (e.g. Customer Type) — replaces platformField
  staticValue:   z.string().optional(),
}).superRefine((m, ctx) => {
  // A mapping needs either an incoming payload field OR a static value
  if (!m.platformField?.trim() && !m.staticValue?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['platformField'], message: 'Platform field is required' })
  }
})

export type FieldMappingData = z.infer<typeof fieldMappingSchema>

// ── Channel form schema ───────────────────────────────────────────────────────

export const channelSchema = z.object({
  name:                 z.string().min(1, 'Channel name is required').max(100),
  type:                 z.enum(['META', 'WEBSITE', 'WHATSAPP', 'REST_API', 'INSTAGRAM', 'GOOGLE'], { message: 'Channel type is required' }),
  accessToken:          z.string().optional(),
  refreshToken:         z.string().optional(),
  webhookSecret:        z.string().optional(),
  apiBaseUrl:           z.string().optional(),
  syncFrequencyMinutes: z.number().int().min(1).max(1440).optional(),
  orgId:                z.string().optional(),
  lobId:                z.string().optional(),
  fieldMappings:        z.array(fieldMappingSchema).optional(),
})

export type ChannelFormData = z.infer<typeof channelSchema>

// A channel must always be associated with a valid Organisation and LOB — for both
// Super Admin and Admin — before it can be CREATED. Only used for the create-mode
// resolver: editing an existing channel never shows/touches the Scope (org/lob)
// section, so the base schema above stays permissive there (also covers any legacy
// channels created before this requirement existed, which may have a null org/lob).
export const channelCreateSchema = channelSchema.extend({
  orgId: z.string().min(1, 'Organisation is required'),
  lobId: z.string().min(1, 'Line of Business is required'),
})

// ── Lead Source schema ────────────────────────────────────────────────────────

export const leadSourceSchema = z.object({
  name:      z.string().min(1, 'Source name is required').max(100),
  channelId: z.string().min(1, 'Channel is required'),
})

export type LeadSourceFormData = z.infer<typeof leadSourceSchema>

// ── Auto Assign Rule schema ───────────────────────────────────────────────────

export const autoAssignRuleSchema = z.object({
  assignedToId: z.string().min(1, 'User is required'),
  priority:     z.number().int().min(1).max(100).optional(),
  condition:    z.string().optional(),
})

export type AutoAssignRuleData = z.infer<typeof autoAssignRuleSchema>
